<template>
  <div>
    <b-overlay :show="loading" rounded="sm" opacity="0.6" no-fade>
      <b-card class="card_body_bottom">
        <template #header>
          <b-row class="flex-row justify-content-start align-items-center" style="width: 100%">
            <b-col cols="12" xl="6" lg="6" md="6" sm="12" class="d-flex justify-content-start align-items-center" style="gap:5px">
              <h4 class="mb-0">Infina Report</h4>
              <b-button
                variant="flat-secondary"
                class="btn-icon rounded-circle"
                :disabled="!selectedCurrency || selectedAccounts.length === 0"
                @click="fetchData"
                v-b-tooltip.hover="'Refresh Report'"
              >
                <feather-icon icon="RefreshCcwIcon" />
              </b-button>
            </b-col>
          </b-row>
        </template>

        <b-card-body>
          <b-row class="align-items-end">
            <!-- Currency -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12">
              <label class="compact_label">Currency</label>
              <v-select
                v-model="selectedCurrency"
                :options="optCurrencies"
                :clearable="false"
                placeholder="Currency"
              />
            </b-col>

            <!-- Accounts -->
            <b-col cols="12" md="6" xl="4" lg="4" sm="12">
              <label class="compact_label">Accounts</label>
              <v-select
                class="compact_form_vue-multi-select"
                v-model="selectedAccounts"
                label="AccountName"
                :options="optAccounts"
                multiple
                :close-on-select="false"
                placeholder="Select account(s)"
              />
            </b-col>

            <!-- Select all accounts -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12" style="padding-bottom: 0.4rem;">
              <b-form-checkbox v-model="allAccountsSelected" @change="toggleAllAccounts">
                All Accounts
              </b-form-checkbox>
            </b-col>

            <!-- Start Date -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12">
              <label class="compact_label">Start Date</label>
              <b-form-datepicker
                v-model="startDate"
                placeholder="Start date"
                :start-weekday="1"
                :hide-header="true"
                class="form-control"
                today-button
                reset-button
              />
            </b-col>

            <!-- End Date -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12">
              <label class="compact_label">End Date</label>
              <b-form-datepicker
                v-model="endDate"
                placeholder="End date"
                :start-weekday="1"
                :hide-header="true"
                :min="startDate"
                class="form-control"
                today-button
                reset-button
              />
            </b-col>
          </b-row>

          <b-row class="mt-1">
            <b-col cols="12" class="d-flex align-items-end" style="gap: 8px;">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="primary"
                size="sm"
                :disabled="!selectedCurrency || selectedAccounts.length === 0"
                @click="fetchData"
              >
                <feather-icon icon="SearchIcon" class="mr-50" />
                <span class="align-middle">Generate Report</span>
              </b-button>

              <b-button
                v-if="rows.length > 0"
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="outline-success"
                size="sm"
                :disabled="downloading"
                @click="downloadXlsx"
              >
                <b-spinner v-if="downloading" small class="mr-50" />
                <feather-icon v-else icon="DownloadIcon" class="mr-50" />
                <span class="align-middle">Download XLSX</span>
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <template #overlay v-if="loading">
        <div class="text-center d-flex justify-content-center align-items-start mt-3">
          <b-icon icon="stopwatch" font-scale="3" animation="cylon" />
          <p id="cancel-label">
            <feather-icon icon="TargetIcon" />
            Preparing report...
          </p>
        </div>
      </template>
    </b-overlay>

    <!-- Results Table -->
    <b-card v-if="rows.length > 0" class="mt-1">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <span class="text-muted" style="font-size: 0.8rem;">
          {{ rows.length }} trade(s)
        </span>
        <span class="text-muted" style="font-size: 0.75rem;">Scroll horizontally to view all columns</span>
      </div>
      <div class="table-responsive">
        <b-table
          class="compact_table infina-report-table"
          :items="rows"
          :fields="tableColumns"
          :small="true"
          :hover="true"
          :striped="true"
          :bordered="true"
          :fixed="false"
          headVariant="dark"
          table-variant="none"
          responsive
          :empty-text="'No trades found for the selected criteria.'"
          show-empty
        >
          <template #cell(ISLEMTIPI)="data">
            <b-badge :variant="data.value === 'ALIS' ? 'light-success' : 'light-danger'">
              {{ data.value }}
            </b-badge>
          </template>
          <template #cell(ISLEMMIKTARI)="data">
            <div class="text-right text-nowrap">{{ formatNumber(data.value) }}</div>
          </template>
          <template #cell(ISLEMBIRIMFIYATI)="data">
            <div class="text-right text-nowrap">{{ data.value }}</div>
          </template>
        </b-table>
      </div>
    </b-card>

    <b-alert v-else-if="searched && rows.length === 0" variant="primary" show class="mt-1">
      <div class="alert-body">
        <span>No trades found for the selected criteria.</span>
      </div>
    </b-alert>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BFormDatepicker, BFormCheckbox,
  BTable, BOverlay, BIcon, BAlert, BSpinner, BBadge, VBTooltip,
} from 'bootstrap-vue';
import store from '@/store';
import { onUnmounted } from '@vue/composition-api';
import vSelect from 'vue-select';
import infinaReportStoreModule from './infinaReportStoreModule';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import Ripple from 'vue-ripple-directive';

const STORE_MODULE = 'infinaReport';

export default {
  components: {
    BCard, BCardBody, BRow, BCol, BButton, BFormDatepicker, BFormCheckbox,
    BTable, BOverlay, BIcon, BAlert, BSpinner, BBadge, vSelect, ToastificationContent,
  },

  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },

  setup() {
    if (!store.hasModule(STORE_MODULE)) {
      store.registerModule(STORE_MODULE, infinaReportStoreModule);
    }
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE)) store.unregisterModule(STORE_MODULE);
    });
  },

  data() {
    return {
      selectedCurrency: 'USD',
      optCurrencies: [],
      selectedAccounts: [],
      optAccounts: [],
      allAccountsSelected: false,
      startDate: null,
      endDate: null,
      rows: [],
      loading: false,
      downloading: false,
      searched: false,
    };
  },

  computed: {
    tableColumns() {
      const w = (px) => ({ minWidth: px + 'px' });
      return [
        { key: 'PORTFOYKODU',     label: 'PORTFOYKODU',     thStyle: w(160) },
        { key: 'ISLEMTARIHI',     label: 'ISLEMTARIHI',     thStyle: w(120) },
        { key: 'MKTANIMI',        label: 'MKTANIMI',        thStyle: w(160) },
        { key: 'ISLEMTIPI',       label: 'ISLEMTIPI',       thStyle: w(100) },
        { key: 'ISLEMMIKTARI',    label: 'ISLEMMIKTARI',    thStyle: w(130) },
        { key: 'ISLEMBIRIMFIYATI',label: 'ISLEMBIRIMFIYATI',thStyle: w(150) },
        { key: 'ARACIKURUM',      label: 'ARACIKURUM',      thStyle: w(140) },
        { key: 'SAKLAMACIKURUM',  label: 'SAKLAMACIKURUM',  thStyle: w(160) },
        { key: 'KIYMETTAKAS',     label: 'KIYMETTAKAS',     thStyle: w(120) },
        { key: 'NAKITTAKAS',      label: 'NAKITTAKAS',      thStyle: w(120) },
        { key: 'HESAPNO',         label: 'HESAPNO',         thStyle: w(120) },
      ];
    },

    queryParams() {
      return {
        currency: this.selectedCurrency,
        accounts: this.selectedAccounts.map((a) => a.AccountName),
        dateStart: this.startDate,
        dateEnd: this.endDate,
      };
    },
  },

  mounted() {
    this.getAccounts();
    this.getCurrencies();
  },

  methods: {
    getAccounts() {
      store
        .dispatch('infinaReport/getInfinaAccounts')
        .then((res) => {
          this.optAccounts = res.data.resData || [];
        })
        .catch((e) => {
          this.showError(`Error fetching accounts: ${e.message}`);
        });
    },

    getCurrencies() {
      store
        .dispatch('infinaReport/getCurrencies')
        .then((res) => {
          this.optCurrencies = (res.data || []).map((c) => c.Currency);
          if (this.optCurrencies.length === 0) {
            this.optCurrencies = ['USD'];
          }
        })
        .catch((e) => {
          this.showError(`Error fetching currencies: ${e.message}`);
        });
    },

    toggleAllAccounts(checked) {
      this.selectedAccounts = checked ? [...this.optAccounts] : [];
    },

    fetchData() {
      if (!this.selectedCurrency || this.selectedAccounts.length === 0) return;
      this.loading = true;
      this.searched = false;

      store
        .dispatch('infinaReport/getInfinaReportData', this.queryParams)
        .then((res) => {
          this.rows = res.data.data || [];
          this.searched = true;
          this.loading = false;
        })
        .catch((e) => {
          this.loading = false;
          this.searched = true;
          this.showError(`Error generating report: ${e.message}`);
        });
    },

    downloadXlsx() {
      if (!this.selectedCurrency || this.selectedAccounts.length === 0) return;
      this.downloading = true;

      store
        .dispatch('infinaReport/downloadInfinaReportXlsx', this.queryParams)
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          const now = new Date();
          const dmy = `${String(now.getDate()).padStart(2, '0')}${String(now.getMonth() + 1).padStart(2, '0')}${now.getFullYear()}`;
          link.setAttribute('download', `INFINA_REPORT_${dmy}.xlsx`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.downloading = false;
        })
        .catch((e) => {
          this.downloading = false;
          this.showError(`Error downloading report: ${e.message}`);
        });
    },

    formatNumber(value) {
      if (value === null || value === '' || value === undefined) return '';
      return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    showError(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Error', text: msg, icon: 'AlertTriangleIcon', variant: 'danger' },
      });
    },
  },

  watch: {
    selectedAccounts(val) {
      this.allAccountsSelected = this.optAccounts.length > 0 && val.length === this.optAccounts.length;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~/src/assets/scss/compact-design.scss";

.infina-report-table {
  font-size: 0.72rem;
}

.card::v-deep .card-body {
  padding: 0.8rem 0.7rem !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
